import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { citiesApi, propertiesApi, articlesApi, formatPrice } from '../../lib/api';
import { PropertyCard } from '../../components/property/PropertyCard';
import { PropertyMap } from '../../components/map/PropertyMap';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  MapPin, Building2, TrendingUp, TrendingDown, Minus, ArrowRight, 
  Home, Newspaper, BarChart3, ChevronRight, Landmark, GraduationCap, 
  Dumbbell, Bus, Palette, ShoppingBag, UtensilsCrossed, TreePine,
  BookOpen, ChevronDown, ChevronUp
} from 'lucide-react';

const ICON_MAP = {
  monument: Landmark, mosque: Landmark, museum: Palette, park: TreePine, historic: Landmark, culture: Palette,
  ecole: GraduationCap, college: GraduationCap, lycee: GraduationCap, universite: GraduationCap, formation: BookOpen,
  sport: Dumbbell, loisir: TreePine, shopping: ShoppingBag, restaurant: UtensilsCrossed,
};

const getIcon = (type) => ICON_MAP[type] || Landmark;

const NeighborhoodCard = ({ neighborhood, language, cityName }) => {
  const [expanded, setExpanded] = useState(false);
  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };
  const getTrendLabel = (trend) => {
    if (language === 'ar') return trend === 'up' ? 'تصاعدي' : trend === 'down' ? 'تنازلي' : 'مستقر';
    return trend === 'up' ? 'En hausse' : trend === 'down' ? 'En baisse' : 'Stable';
  };

  return (
    <Card className="border-0 shadow-md hover:shadow-lg transition-all overflow-hidden" data-testid={`neighborhood-card-${neighborhood.name.toLowerCase().replace(/\s/g, '-')}`}>
      {neighborhood.image && (
        <div className="h-48 overflow-hidden relative">
          <img src={neighborhood.image} alt={neighborhood.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="font-manrope font-bold text-xl text-white">{neighborhood.name}</h3>
            {neighborhood.name_ar && <p className="text-white/70 text-sm font-arabic">{neighborhood.name_ar}</p>}
          </div>
          <Badge className="absolute top-4 right-4 bg-white/90 text-slate-800 flex items-center gap-1">
            {getTrendIcon(neighborhood.trend)} {getTrendLabel(neighborhood.trend)}
          </Badge>
        </div>
      )}
      <CardContent className="p-5">
        {!neighborhood.image && (
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-manrope font-semibold text-lg text-slate-900">{neighborhood.name}</h3>
              {neighborhood.name_ar && <p className="text-sm text-slate-500 font-arabic">{neighborhood.name_ar}</p>}
            </div>
            <Badge variant="secondary" className="flex items-center gap-1">
              {getTrendIcon(neighborhood.trend)} {getTrendLabel(neighborhood.trend)}
            </Badge>
          </div>
        )}

        <p className="text-sm text-slate-600 mb-4 leading-relaxed">
          {language === 'ar' && neighborhood.description_ar ? neighborhood.description_ar : neighborhood.description}
        </p>

        {/* Price Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-600 font-medium mb-1">{language === 'fr' ? 'Prix/m² vente' : 'سعر/م² بيع'}</p>
            <p className="font-manrope font-bold text-blue-900">{formatPrice(neighborhood.avg_price_sqm_sale)}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3">
            <p className="text-xs text-amber-600 font-medium mb-1">{language === 'fr' ? 'Prix/m² location' : 'سعر/م² إيجار'}</p>
            <p className="font-manrope font-bold text-amber-900">{formatPrice(neighborhood.avg_price_sqm_rent)}</p>
          </div>
        </div>

        {/* Ambiance */}
        {neighborhood.ambiance && (
          <div className="bg-slate-50 rounded-lg p-3 mb-4">
            <p className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1">
              <TreePine className="w-3 h-3" />
              {language === 'fr' ? 'Ambiance du quartier' : 'أجواء الحي'}
            </p>
            <p className="text-sm text-slate-700">{language === 'ar' && neighborhood.ambiance_ar ? neighborhood.ambiance_ar : neighborhood.ambiance}</p>
          </div>
        )}

        {/* Transport */}
        {neighborhood.transport && (
          <div className="bg-slate-50 rounded-lg p-3 mb-4">
            <p className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1">
              <Bus className="w-3 h-3" />
              {language === 'fr' ? 'Transport' : 'المواصلات'}
            </p>
            <p className="text-sm text-slate-700">{language === 'ar' && neighborhood.transport_ar ? neighborhood.transport_ar : neighborhood.transport}</p>
          </div>
        )}

        {/* Expandable POI sections */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between text-sm font-semibold text-blue-900 hover:text-blue-700 transition-colors py-2"
          data-testid={`expand-${neighborhood.name.toLowerCase().replace(/\s/g, '-')}`}
        >
          {language === 'fr' ? 'Voir les points d\'intérêt' : 'عرض نقاط الاهتمام'}
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {expanded && (
          <div className="space-y-4 mt-2 animate-in slide-in-from-top-2">
            {/* Monuments */}
            {neighborhood.monuments?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                  <Landmark className="w-4 h-4 text-rose-500" />
                  {language === 'fr' ? 'Monuments & Patrimoine' : 'المعالم والتراث'}
                </h4>
                <div className="space-y-2">
                  {neighborhood.monuments.map((m, i) => {
                    const Icon = getIcon(m.type);
                    return (
                      <div key={i} className="flex items-start gap-2 bg-rose-50/50 rounded-lg p-2.5">
                        <Icon className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{m.name}</p>
                          {m.description && <p className="text-xs text-slate-500 mt-0.5">{m.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Schools */}
            {neighborhood.schools?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                  <GraduationCap className="w-4 h-4 text-indigo-500" />
                  {language === 'fr' ? 'Éducation' : 'التعليم'}
                </h4>
                <div className="space-y-2">
                  {neighborhood.schools.map((s, i) => {
                    const Icon = getIcon(s.type);
                    return (
                      <div key={i} className="flex items-start gap-2 bg-indigo-50/50 rounded-lg p-2.5">
                        <Icon className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{s.name}</p>
                          {s.description && <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Activities */}
            {neighborhood.activities?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
                  <Dumbbell className="w-4 h-4 text-emerald-500" />
                  {language === 'fr' ? 'Activités & Loisirs' : 'الأنشطة والترفيه'}
                </h4>
                <div className="space-y-2">
                  {neighborhood.activities.map((a, i) => {
                    const Icon = getIcon(a.type);
                    return (
                      <div key={i} className="flex items-start gap-2 bg-emerald-50/50 rounded-lg p-2.5">
                        <Icon className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{a.name}</p>
                          {a.description && <p className="text-xs text-slate-500 mt-0.5">{a.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* View listings button */}
        <Link to={`/search?city=${cityName}&neighborhood=${neighborhood.name}`} className="block mt-4">
          <Button variant="outline" className="w-full gap-2 hover:bg-blue-50 hover:text-blue-900 hover:border-blue-200">
            {language === 'fr' ? 'Voir les annonces' : 'عرض الإعلانات'}
            <ChevronRight className="w-4 h-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

export const CityDetailPage = () => {
  const { cityName } = useParams();
  const { t, language } = useLanguage();
  const [city, setCity] = useState(null);
  const [properties, setProperties] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [cityName]);

  const loadData = async () => {
    setLoading(true);
    try {
      const capitalizedCity = cityName.charAt(0).toUpperCase() + cityName.slice(1);
      const [cityRes, propsRes, articlesRes] = await Promise.all([
        citiesApi.get(capitalizedCity),
        propertiesApi.list({ city: capitalizedCity, limit: 6 }),
        articlesApi.list({ city: capitalizedCity, limit: 3 }),
      ]);
      setCity(cityRes.data);
      setProperties(propsRes.data.properties || []);
      setArticles(articlesRes.data || []);
    } catch (error) {
      console.error('Failed to load city data:', error);
    } finally {
      setLoading(false);
    }
  };

  const cityImages = {
    'alger': 'https://images.unsplash.com/photo-1675610137184-40f1bce58d71?w=1200',
    'oran': 'https://images.unsplash.com/photo-1706002027900-a1be40d0627e?w=1200',
    'béjaïa': 'https://images.unsplash.com/photo-1630838788292-d9c78b6a0f2f?w=1200',
    'tizi ouzou': 'https://images.unsplash.com/photo-1605692024639-04377ef2fd90?w=1200',
    'annaba': 'https://images.unsplash.com/photo-1760726743931-24bd760fe554?w=1200',
  };

  const citySeoTitle = city ? (language === 'fr' ? `Immobilier a ${city.city} - Guide, prix, quartiers | Beytam` : `عقارات ${city.city} - دليل، أسعار، أحياء | بيتم`) : 'Beytam';
  const citySeoDesc = city ? (language === 'fr' ? `Guide immobilier de ${city.city}. Prix moyen: ${formatPrice(city.avg_price_sqm_sale)}/m2. ${city.total_listings} annonces.` : `دليل عقارات ${city.city}. ${city.total_listings} إعلان.`) : '';

  useSEO({
    title: citySeoTitle,
    description: citySeoDesc,
    ogTitle: citySeoTitle,
    canonical: window.location.href,
  });

  if (loading) {
    return (
      <div className="min-h-screen">
        <Skeleton className="h-80 w-full" />
        <div className="container mx-auto px-4 py-12">
          <Skeleton className="h-8 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-80 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">{language === 'fr' ? 'Ville non trouvée' : 'المدينة غير موجودة'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="city-detail-page">
      {/* Hero */}
      <div className="relative h-80 md:h-[420px]">
        <img
          src={cityImages[cityName.toLowerCase()] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200'}
          alt={city.city}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-white/80" />
              <span className="text-white/80 text-sm">{language === 'fr' ? 'Algérie' : 'الجزائر'}</span>
            </div>
            <h1 className="font-manrope text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3">
              {city.city}
            </h1>
            <p className="text-white/85 max-w-3xl text-base md:text-lg leading-relaxed">
              {language === 'ar' && city.description_ar ? city.description_ar : city.description}
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">{t('cities_listings')}</p>
              <p className="font-manrope text-2xl font-bold text-blue-900">{city.total_listings}</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">{language === 'fr' ? 'Prix/m² vente' : 'سعر/م² بيع'}</p>
              <p className="font-manrope text-lg font-bold text-blue-900">{formatPrice(city.avg_price_sqm_sale)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">{language === 'fr' ? 'Prix/m² location' : 'سعر/م² إيجار'}</p>
              <p className="font-manrope text-lg font-bold text-amber-700">{formatPrice(city.avg_price_sqm_rent)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">{t('cities_neighborhoods')}</p>
              <p className="font-manrope text-2xl font-bold text-blue-900">{city.neighborhoods.length}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="neighborhoods" className="space-y-8">
          <TabsList className="bg-slate-100 p-1 rounded-full w-fit">
            <TabsTrigger value="neighborhoods" className="gap-2 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-neighborhoods">
              <BarChart3 className="w-4 h-4" />
              {language === 'fr' ? 'Guide des quartiers' : 'دليل الأحياء'}
            </TabsTrigger>
            <TabsTrigger value="properties" className="gap-2 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-properties">
              <Home className="w-4 h-4" />
              {t('cities_listings')}
            </TabsTrigger>
            <TabsTrigger value="articles" className="gap-2 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-articles">
              <Newspaper className="w-4 h-4" />
              {t('nav_blog')}
            </TabsTrigger>
          </TabsList>

          {/* Neighborhoods Tab - Comprehensive Guide */}
          <TabsContent value="neighborhoods" data-testid="neighborhoods-tab">
            <div className="mb-6">
              <h2 className="font-manrope text-2xl font-bold text-slate-900 mb-2">
                {language === 'fr' ? `Découvrez les quartiers de ${city.city}` : `اكتشف أحياء ${city.city}`}
              </h2>
              <p className="text-slate-600">
                {language === 'fr' 
                  ? 'Monuments, écoles, activités, prix au m² : tout ce que vous devez savoir pour choisir votre quartier.'
                  : 'المعالم، المدارس، الأنشطة، سعر المتر المربع: كل ما تحتاج معرفته لاختيار حيك.'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {city.neighborhoods.map((neighborhood) => (
                <NeighborhoodCard
                  key={neighborhood.name}
                  neighborhood={neighborhood}
                  language={language}
                  cityName={city.city}
                />
              ))}
            </div>
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties" data-testid="properties-tab">
            {properties.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {properties.map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
                <div className="text-center">
                  <Link to={`/search?city=${city.city}`}>
                    <Button className="bg-blue-900 hover:bg-blue-800 rounded-full gap-2 px-6">
                      {language === 'fr' ? 'Voir toutes les annonces' : 'عرض جميع الإعلانات'}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{language === 'fr' ? 'Aucune annonce pour le moment' : 'لا توجد إعلانات حالياً'}</p>
              </div>
            )}
          </TabsContent>

          {/* Articles Tab */}
          <TabsContent value="articles" data-testid="articles-tab">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link key={article.id} to={`/blog/${article.slug}`}>
                    <Card className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-all h-full group">
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={article.image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <CardContent className="p-5">
                        <Badge variant="secondary" className="mb-2">{article.category}</Badge>
                        <h3 className="font-semibold text-slate-900 line-clamp-2 group-hover:text-blue-900 transition-colors">
                          {language === 'ar' && article.title_ar ? article.title_ar : article.title}
                        </h3>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{language === 'fr' ? 'Aucun article pour le moment' : 'لا توجد مقالات حالياً'}</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Map */}
        <div className="mt-12">
          <h2 className="font-manrope text-2xl font-bold text-slate-900 mb-6">
            {language === 'fr' ? 'Carte des annonces' : 'خريطة الإعلانات'}
          </h2>
          <div className="h-[400px] rounded-xl overflow-hidden shadow-md">
            <PropertyMap 
              properties={properties} 
              center={[city.latitude, city.longitude]}
              zoom={12}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
